/** @jsx jsx */
import { Modal, Spacer, TextGroupMedium } from "@cd2/components/build/es6"
import { P } from "@cd2/text/build/es6"
import { assert } from "@cd2/tools/build/es6"
import { jsx, css } from "@emotion/core"
import React, { useState } from "react"
import { ConfirmCancelButtons } from "../Components/Buttons/ConfirmCancelButtons"
import { useGlobalState } from "../Components/GlobalStateProvider"
import { InternalOrderNoBarscanStatus } from "../Pages/GoodsReceiptsIndexPage"
import { IRecord } from "../types/schema"
import { useApi } from "./useApi"
import { useConfirmation } from "./useConfirmation"
import { useSiteAlerts } from "./useSiteAlerts"
import { IOrder, iOrderAttrs } from "../types/useExportInternalOrder"
import { differenceInMinutes } from "date-fns"
import { renderTable } from "../Components/renderTable"
import { useAsyncCallback } from "./useAsyncCallback"
import { Fields } from "@cd2/forms"
import { extractMessageFromError } from "../../server/utils/extractMessageFromError"

export function useExportInternalOrder() {
  const api = useApi()
  const { currentUser } = useGlobalState()
  const skeem = window.skeem
  const siteAlerts = useSiteAlerts()

  // MARK: getDefaultStockLocation

  // MARK: loadOrder
  async function loadOrder(orderId: string) {
    const order = await skeem.fetch2<IOrder | null>({
      first: {
        name: "orders",
        attributes: iOrderAttrs,
        matches: { id: orderId },
      },
    })
    assert(order, "Order not found")

    return order
  }

  // MARK: getItemStock
  async function getItemStock(item: {
    product?: { id: string } | null
    quantity: number | null
    product_location?: { id: string } | null
  }) {
    if (!item.product || !item.quantity || !item.product_location) {
      return 0
    }
    const itemStock = await api.productLocationsStock([item.product_location.id])
    return itemStock[item.product_location.id]?.inStock ?? 0
  }

  type Item = IRecord<
    "order_items",
    "product" | "product_location" | "quantity" | "stock_code" | "body" | "type"
  >

  type ItemWithStock = Item & { inStock: number }

  // MARK: getItemsWithStock
  async function getItemsWithStock(itemIds: string[]): Promise<ItemWithStock[]> {
    const items = await skeem.fetch2<Item[]>({
      model: {
        name: "order_items",
        attributes: ["stock_code", "quantity", "product_location", "product", "body", "type"],
        filter: { matches: { id: { or: itemIds }, product: { not: null } } },
      },
    })
    if (!items.length) {
      return []
    }
    const itemsInStock = await Promise.all(
      items.map(async item => {
        const inStock = await getItemStock(item)
        return { ...item, inStock }
      }),
    )
    return itemsInStock
  }

  async function getShortfallItems(itemIds: string[]) {
    const itemsWithStock = await getItemsWithStock(itemIds)
    return itemsWithStock.filter(item => item.inStock < (item.quantity ?? 1))
  }

  // MARK: itemsHaveShortfall
  async function itemsHaveShortfall(itemIds: string[]) {
    const items = await getShortfallItems(itemIds)
    return items.length > 0
  }

  async function validateOrderCanExport(
    orderId: string,
    args?: { doNotCheckForShortfall?: boolean },
  ) {
    const order = await skeem.fetch2<
      IRecord<"orders", "internal_branch_transfer_status" | "items" | "sage_exported_at">
    >({
      first: {
        name: "orders",
        attributes: ["internal_branch_transfer_status", "items", "sage_exported_at"],
        matches: { id: orderId },
      },
    })

    const canReExportSource =
      !order.sage_exported_at ||
      differenceInMinutes(new Date(), new Date(order.sage_exported_at)) > 10

    const exportingStatuses = [
      "exporting_target",
      !canReExportSource && "exporting_source",
      "complete",
    ].filter(Boolean)

    assert(
      // No status or status is not one of an exporting or complete state.
      !order.internal_branch_transfer_status ||
        !exportingStatuses.includes(order.internal_branch_transfer_status),
      "Order is already being exported",
    )
    if (!args?.doNotCheckForShortfall) {
      const hasShortfall = await itemsHaveShortfall(order.items.map(x => x.id))
      assert(!hasShortfall, "Order items have a shortfall")
    }
  }

  const [confirmRemoveConsolidationKeyNode, triggerRemoveConsolidationKeyDialog] = useConfirmation<
    void,
    boolean
  >(({ onResolve }) => (
    <Modal
      render={() => (
        <div>
          <TextGroupMedium
            heading="Order will be removed from consolidation"
            body={
              <React.Fragment>
                <Spacer />
                <P>This order is flagged to export via consolidation.</P>
                <P>
                  Exporting this order will remove it from the consolidation and export it
                  individually.
                </P>
              </React.Fragment>
            }
          />
          <Spacer />
          <ConfirmCancelButtons
            onCancel={() => onResolve(false)}
            onConfirm={() => onResolve(true)}
            buttonOpts={{
              confirm: { children: "Continue" },
              cancel: { children: "Cancel" },
            }}
          />
        </div>
      )}
      isOpen
    />
  ))

  // MARK: handle export
  async function handleExport(orderId: string): Promise<boolean> {
    // Check we are still able to export this order before proceeding
    await validateOrderCanExport(orderId, {
      doNotCheckForShortfall: true,
    })

    assert(currentUser?.id, "Current user has not loaded")
    let order = await loadOrder(orderId)

    const productItems = order.items.filter(item => item.product)
    assert(
      productItems.every(item => item.product.sage_id),
      "Order has unexported products",
    )
    assert(
      productItems.every(item => item.product_location),
      "Some items have missing locations",
    )
    assert(
      productItems.every(item => item.quantity),
      "Some items have missing quantities",
    )

    if (order.consolidated_orders_key) {
      const shouldContinue = await triggerRemoveConsolidationKeyDialog()
      if (!shouldContinue) {
        return false
      }

      await skeem.mutate({
        orders: { update: { id: order.id, consolidated_orders_key: "" } },
      })
      order = await loadOrder(orderId)
    }

    const shortfallItems = await getShortfallItems(productItems.map(x => x.id))
    if (shortfallItems.length) {
      const handle = await triggerCreateBackOrder({ orderId: order.id, items: shortfallItems })
      if (!handle) {
        return false
      }
    }

    const result = await api.exportInternalOrder({ id: orderId })
    return result.success
  }

  function canExportFromSource(order: {
    internal_branch_transfer_status: string
    sage_exported_at?: string | Date | null
  }) {
    const exportTimeExceeded =
      order.sage_exported_at == null ||
      differenceInMinutes(new Date(), new Date(order.sage_exported_at)) > 10

    const hasExportStatus = !!order.internal_branch_transfer_status

    if (order.internal_branch_transfer_status === "exporting_source" && exportTimeExceeded) {
      return true
    }

    // If the order has no export status, it can be exported.
    if (!hasExportStatus) {
      return true
    }

    // If the order previously failed to export, allow re-exporting.
    if (order.internal_branch_transfer_status === "failed_source") {
      return true
    }

    if (order.internal_branch_transfer_status === "processing_consolidation") {
      return true
    }

    // Otherwise, it cannot be exported.
    return false
  }

  const [backOrderNode, triggerCreateBackOrder] = useConfirmation<
    { orderId: string; items: ItemWithStock[] },
    boolean
  >(({ onResolve, data }) => {
    const api = useApi()
    const [state, setState] = useState({
      items: data.items.map(item => ({
        ...item,
        quantity: item.quantity ?? 0,
        backOrderQuantity: (item.quantity! - item.inStock) as number | null,
      })),
      requestError: "",
      errors: {} as Record<string /* item.id */, string>,
    })

    const setBackOrderQuantity = (id: string, value: number | null) => {
      setState(prevState => ({
        ...prevState,
        items: prevState.items.map(item => {
          if (item.id === id) {
            return { ...item, backOrderQuantity: value }
          }
          return item
        }),
      }))
    }

    const handleSubmit = useAsyncCallback(async () => {
      const errors = {} as (typeof state)["errors"]
      const backOrderItems = [] as { id: string; quantityToMove: number }[]

      for (const item of state.items) {
        if (!item.backOrderQuantity) {
          errors[item.id] = "back order quantity is required"
        } else if (item.backOrderQuantity < 0) {
          errors[item.id] = "back order quantity must be greater than 0"
        } else if (item.backOrderQuantity > item.quantity) {
          errors[item.id] = "back order quantity must be less than or equal to requested quantity"
        } else if (item.quantity - item.backOrderQuantity > item.inStock) {
          errors[item.id] = "not enough stock to fulfil original order"
        } else {
          backOrderItems.push({
            id: item.id,
            quantityToMove: item.backOrderQuantity,
          })
        }
      }

      setState(prevState => ({
        ...prevState,
        requestError: "",
        errors: errors,
      }))
      if (Object.keys(errors).length) {
        return
      }

      try {
        await api.createBackOrder({
          fromOrderId: data.orderId,
          items: backOrderItems,
        })
        siteAlerts.genericSuccess("Back order created")

        onResolve(true)
      } catch (e) {
        console.error(e)
        const message = extractMessageFromError(e)
        siteAlerts.genericError("Failed to create back order")
        setState(prevState => ({
          ...prevState,
          requestError: message,
        }))
      }
    })

    return (
      <Modal
        render={() => (
          <div>
            <TextGroupMedium
              heading="Items with shortfall"
              body={
                <P>
                  One or more items on this order have a shortfall. The following items will be
                  placed onto a back order.
                </P>
              }
            />
            <Spacer />
            {state.requestError && <P color="red">{state.requestError}</P>}
            {renderTable({
              compact: true,
              data: state.items,
              columns: [
                {
                  heading: "Item",
                  render: row => (
                    <div>
                      {row.stock_code && <P size="small">{row.stock_code}</P>}
                      {row.body && <P>{row.body}</P>}
                    </div>
                  ),
                },
                { heading: "Quantity Requested", render: row => row.quantity },
                { heading: "Available", render: row => row.inStock },
                {
                  heading: "Back order quantity",
                  render: row => (
                    <Fields.Number2
                      value={row.backOrderQuantity}
                      onChange={value => setBackOrderQuantity(row.id, value)}
                      error={state.errors[row.id]}
                    />
                  ),
                },
                {
                  heading: "To export",
                  render: row => {
                    const newQuantity = row.quantity - (row.backOrderQuantity ?? 0)
                    return (
                      <div
                        css={css`
                          ${newQuantity > row.inStock &&
                          css`
                            color: red;
                          `}
                        `}
                      >
                        {newQuantity}
                      </div>
                    )
                  },
                },
              ],
            })}
            <Spacer />
            <ConfirmCancelButtons
              onCancel={() => onResolve(false)}
              onConfirm={handleSubmit}
              buttonOpts={{ confirm: { children: "Save and create back order" } }}
            />
          </div>
        )}
        isOpen
      />
    )
  })

  return {
    handleExportSource: handleExport,
    canExportFromSource,
    itemsHaveShortfall,
    modalNode: (
      <React.Fragment>
        {backOrderNode}
        {confirmRemoveConsolidationKeyNode}
      </React.Fragment>
    ),
  }
}

export function parseProductSageId(id?: string) {
  // example sage_id: "9d9f9bdc-7b5e-4b2a-8aa1-26ea6795ae13-222566"
  const parts = (id ?? "").split("-")
  assert(parts.length === 6, `Unable to parse product Sage ID: ${id}`)

  const idAsNum = parseInt(parts[5])
  assert(!isNaN(idAsNum), `Unable to parse product Sage ID: ${id}, ${idAsNum}`)

  return idAsNum
}

export function silentParseProductSageId(id?: string) {
  try {
    return parseProductSageId(id)
  } catch (e) {
    console.warn(e)
    return null
  }
}

// internal_branch_transfer_json
// internal_branch_transfer_json_response
// internal_branch_transfer_status

export function getInternalOrderFriendlyStatusName(status: string, nullIfUnknown = false) {
  if (status == "processing_order_request_consolidation") {
    return "Consolidating Order Request"
  }
  if (status == "processing_consolidation") {
    return "Consolidating"
  }
  if (status === "exported") {
    return "Pending"
  }
  if (status === "exporting_source") {
    return "Exporting"
  }
  if (status === "exporting_target") {
    return "Exporting"
  }
  if (status === "failed_source") {
    return "Failed to export"
  }
  if (status === "failed_target") {
    return "Failed"
  }
  if (status === "partially_exported") {
    return "Partially Exported"
  }
  if (status === "complete") {
    return "Complete"
  }
  if (status === "cancelled") {
    return "Cancelled"
  }
  if (status === InternalOrderNoBarscanStatus.picking) {
    return "Picking"
  }
  if (status === InternalOrderNoBarscanStatus.despatched) {
    return "Despatched"
  }
  if (status === InternalOrderNoBarscanStatus.exported) {
    return "Complete"
  }
  return nullIfUnknown ? null : "Unknown status"
}
